/**
 * Monthly payables calendar — the published schedule, verbatim.
 *
 * Dates are NOT generated or predicted. Accounting publishes the calendar and
 * PUBLISHED_CYCLES mirrors it exactly, so what tenants and managers see is
 * always the official table. When the next schedule is published, append its
 * rows here — nothing else needs to change.
 */

export const CALENDAR_RULE_NOTE =
  "Submission dates are 2 business days prior to payment date, with approvals done 1 " +
  "business day prior to payment. Any payable day that landed on a Monday or holiday was " +
  "pushed to the following business day to allow for approval process.";

/** The published Aug–Dec 2026 schedule, in payment-date order. */
export const PUBLISHED_CYCLES = [
  { month: "August", year: 2026, submitBy: "2026-08-04", approveBy: "2026-08-05", paymentDate: "2026-08-06" },
  { month: "August", year: 2026, submitBy: "2026-08-18", approveBy: "2026-08-19", paymentDate: "2026-08-20" },
  { month: "September", year: 2026, submitBy: "2026-09-03", approveBy: "2026-09-04", paymentDate: "2026-09-08" },
  { month: "September", year: 2026, submitBy: "2026-09-18", approveBy: "2026-09-21", paymentDate: "2026-09-22" },
  { month: "October", year: 2026, submitBy: "2026-10-02", approveBy: "2026-10-05", paymentDate: "2026-10-06" },
  { month: "October", year: 2026, submitBy: "2026-10-16", approveBy: "2026-10-19", paymentDate: "2026-10-20" },
  { month: "November", year: 2026, submitBy: "2026-11-03", approveBy: "2026-11-04", paymentDate: "2026-11-05" },
  { month: "November", year: 2026, submitBy: "2026-11-17", approveBy: "2026-11-18", paymentDate: "2026-11-19" },
  { month: "December", year: 2026, submitBy: "2026-12-04", approveBy: "2026-12-07", paymentDate: "2026-12-08" },
  { month: "December", year: 2026, submitBy: "2026-12-18", approveBy: "2026-12-21", paymentDate: "2026-12-22" },
];

const utc = (y, m, d) => new Date(Date.UTC(y, m, d));
const ymd = (date) => date.toISOString().slice(0, 10);

/**
 * The viewer's own calendar day as a UTC-midnight date. Converting `new Date()`
 * with `toISOString()` directly would read as tomorrow through a Mountain-Time
 * evening and hide a cycle paying today.
 */
export const localToday = () => {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
};

/** The full published schedule. Options are accepted for API compatibility. */
export const buildPayablesCalendar = () => [...PUBLISHED_CYCLES];

/**
 * Cycles still to come. A cycle counts as upcoming right up to and including
 * its payment date; once the published list is exhausted this returns [].
 */
export const upcomingCycles = ({ today = localToday(), limit } = {}) => {
  const todayStr = ymd(today);
  const rows = PUBLISHED_CYCLES.filter((c) => c.paymentDate >= todayStr);
  return typeof limit === "number" ? rows.slice(0, limit) : rows;
};

/** The cycle an invoice submitted today would fall into. */
export const currentCycle = ({ today = localToday() } = {}) => {
  const todayStr = ymd(today);
  return PUBLISHED_CYCLES.find((c) => c.submitBy >= todayStr) || null;
};

/** "Aug 4, 2026" — matches the published calendar's formatting. */
export const formatCycleDate = (value) => {
  if (!value) return "—";
  const [y, m, d] = String(value).split("-").map(Number);
  if (!y || !m || !d) return "—";
  return utc(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};
